// Setup › Add-ons (`/c/{slug}/setup/addons`; design spec §4 Setup WORKSPACE, §9; coverage
// audit §5 row 4 and the spec corrections). One row per add-on of the catalog: its state for this
// company, what it depends on and what depends on it.
// - Agency operators switch an add-on on or off here through the one endpoint that exists,
//   `PUT /v1/operator/companies/{id}/entitlements` (dependencies enforced by the server).
// - Owners and admins see the state and "Managed by your CompanyOS operator": there is no
//   request endpoint (audit row 105), so no request button.
// - An add-on that is not sold yet reads "Not available yet" and never offers Enable (the API
//   would answer `addon_inactive`).
// After a switch, `/v1/me` is refetched at once, so the rail, the sidebars and the routes flip
// without a reload.
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  HomeCardRow,
  Notice,
  SettingsGroup,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import type { Api } from "../../api/client";
import type { EntitlementStatus, MeResponse } from "../../api/types";
import type { CompanyAccess } from "../company";
import { EntitlementBadge, problemText } from "../ops-parts";
import { entitlementsQuery, opsKeys, todayUtc } from "../ops-queries";
import { SetupPage } from "../settings/setup-page";
import { featureState, useSession } from "../use-feature";
import { ADDON_CATALOG, type AddonKey, type AddonSpec, addonSpec, dependents } from "./catalog";

type Source = "plan" | "addon" | "trial" | "manual";

export type EntitlementChange = {
  feature: AddonKey;
  status: EntitlementStatus;
  source?: Source;
  note?: string | null;
};

/** The operator's switch: one PUT, then `/v1/me` and the entitlement rows are read again. */
export function useSetEntitlement(api: Api, companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (change: EntitlementChange) =>
      api.client.operator.entitlements.put({
        params: { company_id: companyId },
        body: {
          feature_key: change.feature,
          status: change.status,
          source: change.source ?? (change.status === "trial" ? "trial" : "addon"),
          starts_on: todayUtc(),
          ...(change.note ? { note: change.note } : {}),
        },
      }),
    onSuccess: async (data, change) => {
      qc.setQueryData(opsKeys.entitlements(companyId), data);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["me"] }),
        qc.invalidateQueries({ queryKey: ["operator", "companies"] }),
      ]);
      const name = addonSpec(change.feature)?.name ?? change.feature;
      toast.add({
        title: change.status === "disabled" ? `${name} is off` : `${name}: ${change.status}`,
        type: "success",
      });
    },
  });
}

/** The add-on's own status from `/v1/me` (its row in `entitlement_cache`). */
export function ownStatus(me: MeResponse, companyId: string, key: AddonKey): EntitlementStatus {
  return me.entitlements[companyId]?.[key] ?? "disabled";
}

const ON = new Set<EntitlementStatus>(["active", "trial", "grace"]);

export function AddonsPage({ api, company }: { api: Api; company: CompanyAccess }) {
  const { me } = useSession();
  const operator = company.roles.includes("agency_operator");
  // Operators also read the live rows: grace dates and notes (owners have only `/v1/me`).
  const live = useQuery({ ...entitlementsQuery(api, company.id), enabled: operator });
  const setEntitlement = useSetEntitlement(api, company.id);
  // Deep links from a locked rail item (`#kpi`) land on the add-on's row.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(`addon-${id}`)?.scrollIntoView?.({ block: "center" });
  }, []);

  return (
    <SetupPage title="Add-ons">
      {setEntitlement.isError ? (
        <Notice tone="danger" role="alert">
          {problemText(setEntitlement.error, "The add-on could not be switched.")}
        </Notice>
      ) : null}
      <SettingsGroup title="Add-ons" count={ADDON_CATALOG.length}>
        {operator ? null : (
          <p
            data-slot="addons-managed"
            className="border-b border-subtle px-4 py-2 text-xs text-muted-foreground"
          >
            Add-ons are switched on and off by your CompanyOS operator; ask them to enable one.
          </p>
        )}
        {ADDON_CATALOG.map((spec) => (
          <AddonRow
            key={spec.key}
            spec={spec}
            me={me}
            companyId={company.id}
            operator={operator}
            graceUntil={
              live.data?.cache.find((c) => c.feature_key === spec.key)?.read_only_until ?? null
            }
            slug={company.slug}
            pending={setEntitlement.isPending && setEntitlement.variables?.feature === spec.key}
            onChange={(status) => setEntitlement.mutate({ feature: spec.key, status })}
          />
        ))}
      </SettingsGroup>
    </SetupPage>
  );
}

function dependencyText(spec: AddonSpec): string | null {
  const parts = spec.dependencies.map((d) => {
    const name = addonSpec(d.key)?.name ?? d.key;
    return d.mode === "bundles" ? `Includes ${name}` : `Needs ${name}`;
  });
  const by = dependents(spec.key).map((a) => a.name);
  if (by.length) parts.push(`${by.join(", ")} ${by.length === 1 ? "depends" : "depend"} on it`);
  return parts.length ? parts.join(" · ") : null;
}

export function AddonRow({
  spec,
  me,
  companyId,
  operator,
  graceUntil,
  slug,
  pending,
  onChange,
}: {
  spec: AddonSpec;
  me: MeResponse;
  companyId: string;
  operator: boolean;
  graceUntil: string | null;
  /** WF-CONTROL: the company's slug, for the Workforce Control row's link to its Setup page. */
  slug?: string;
  pending: boolean;
  onChange: (status: EntitlementStatus) => void;
}) {
  const status = ownStatus(me, companyId, spec.key);
  const effective = featureState(me, companyId, spec.key);
  const on = ON.has(status);
  // Switching off is refused while an add-on that depends on this one is on (`addon_required_by`).
  const blockers = dependents(spec.key).filter((d) => ON.has(ownStatus(me, companyId, d.key)));
  const deps = dependencyText(spec);
  // One line: the dependencies and any state worth knowing; what it is sits in the tooltip.
  const description =
    [
      deps,
      on && !effective.enabled ? "Off until the add-ons it needs are on" : null,
      status === "grace" && graceUntil ? `Read-only until ${graceUntil}, then hidden` : null,
    ]
      .filter(Boolean)
      .join(" · ") || spec.description;

  let trailing: ReactNode;
  if (!spec.active && !on) {
    trailing = (
      <Badge variant="outline" data-status="unavailable">
        Not available yet
      </Badge>
    );
  } else {
    trailing = (
      <>
        {/* WF-CONTROL: an owner switches Workforce Control on its own Setup page. */}
        {spec.key === "workforce_plugin" && slug ? (
          <Link
            to="/c/$slug/setup/claude-code"
            params={{ slug }}
            className="text-xs text-primary-text underline-offset-4 hover:underline"
            data-slot="addon-setup-link"
          >
            Set up
          </Link>
        ) : null}
        <EntitlementBadge status={status} />
        {operator ? (
          <>
            {status !== "active" && spec.active ? (
              <Button size="sm" disabled={pending} onClick={() => onChange("active")}>
                {pending ? "Saving…" : status === "disabled" ? "Enable" : "Activate"}
                <span className="sr-only"> {spec.name}</span>
              </Button>
            ) : null}
            {on ? (
              <TurnOffDialog
                spec={spec}
                pending={pending}
                blockedBy={blockers.map((b) => b.name)}
                graceOffered={status !== "grace"}
                onConfirm={onChange}
              />
            ) : null}
          </>
        ) : null}
      </>
    );
  }

  return (
    <HomeCardRow
      id={`addon-${spec.key}`}
      data-addon={spec.key}
      className="scroll-mt-24 target:bg-primary-soft"
      title={
        <span className="font-medium" title={spec.description}>
          {spec.name}
        </span>
      }
      description={<span className="block truncate max-md:whitespace-normal">{description}</span>}
      trailing={trailing}
    />
  );
}

function TurnOffDialog({
  spec,
  pending,
  blockedBy,
  graceOffered,
  onConfirm,
}: {
  spec: AddonSpec;
  pending: boolean;
  blockedBy: string[];
  graceOffered: boolean;
  onConfirm: (status: EntitlementStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const blocked = blockedBy.length > 0;
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        variant="secondary"
        size="sm"
        disabled={pending || blocked}
        title={blocked ? `Turn off ${blockedBy.join(", ")} first` : undefined}
        onClick={() => setOpen(true)}
      >
        Turn off<span className="sr-only"> {spec.name}</span>
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Turn off {spec.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Its screens disappear for everyone in the company and its API answers "not enabled". No
            data is deleted; turning it back on restores everything.
            {graceOffered
              ? " Read-only keeps the screens visible without changes for the grace period."
              : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {graceOffered ? (
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                onConfirm("grace");
              }}
            >
              Read-only
            </Button>
          ) : null}
          <Button
            variant="destructive"
            onClick={() => {
              setOpen(false);
              onConfirm("disabled");
            }}
          >
            Turn off
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
